<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import Icon from "@/javascript/views/components/Icon.vue";
import {useReportStore} from "../../../stores/report";
import {useOrganizationStore} from "../../../stores/organizations";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import Popup from "../../components/popup.vue";

export default {
  name: 'DonationReportExporter',
  components: {Popup, Icon, LoadingSpinner, VueDatePicker},
  props: {
    title: {
      type: String,
      default: "Giving Report"
    },
    description: {
      type: String,
      required: false
    }
  },
  emits: ['close', 'generate-report'],
  setup() {
    const reportStore = useReportStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      reportStore,
      organizationStore,
      analyticEventStore
    };
  },
  data() {
    return {
      startDate: '',
      endDate: '',
      generating: false,
      popupMessage: ""
    };
  },
  mounted() {
    // Set end date to today by default
    const today = new Date().toISOString().split('T')[0];
    this.endDate = today;
  },
  methods: {
    displayDate(date) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: false,
        time: false
      };

      return ApplicationHelper.formatDate(date, options);
    },

    // Sets start and end date based on some quick select options
    setDateRange(period) {
      const today = new Date();
      const endDate = new Date(today);

      switch (period) {
        case 'lastWeek':
          // Get last full calendar week (Monday to Sunday)
          const lastSunday = new Date(today);
          lastSunday.setDate(today.getDate() - today.getDay() - 1);

          const lastMonday = new Date(lastSunday);
          lastMonday.setDate(lastSunday.getDate() - 6);

          this.startDate = lastMonday;
          this.endDate = lastSunday;
          break;

        case 'lastMonth':
          // Get last full calendar month
          const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

          this.startDate = lastMonthStart;
          this.endDate = lastMonthEnd;
          break;

        case 'lastYear':
          // YDT from Jan 1
          const yearStart = new Date(today.getFullYear(), 0, 1);

          this.startDate = yearStart;
          this.endDate = today;
          break;

        case 'currentFiscal': {
          // starts at fiscal year start, ends today
          const fyDate = this.organizationStore.organization?.fiscal_year_start_date;
          //Default to Jan 1st if no custom fiscal is set
          const fy = fyDate ? new Date(fyDate + 'T00:00:00') : new Date(today.getFullYear(), 0, 1);
          let fyStart = new Date(today.getFullYear(), fy.getMonth(), fy.getDate());
          if (fyStart > today) fyStart.setFullYear(fyStart.getFullYear() - 1);
          this.startDate = fyStart;
          this.endDate = today;
          break;
        }

        case 'previousFiscal': {
          //   starts one year before current FY start, ends day before current FY start
          const fyDate = this.organizationStore.organization?.fiscal_year_start_date;
          //Default to Jan 1st if no custom fiscal is set
          const fy = fyDate ? new Date(fyDate + 'T00:00:00') : new Date(today.getFullYear(), 0, 1);
          let fyStart = new Date(today.getFullYear(), fy.getMonth(), fy.getDate());
          if (fyStart > today) fyStart.setFullYear(fyStart.getFullYear() - 1);
          const prevStart = new Date(fyStart);
          prevStart.setFullYear(prevStart.getFullYear() - 1);
          const prevEnd = new Date(fyStart);
          prevEnd.setDate(prevEnd.getDate() - 1);
          this.startDate = prevStart;
          this.endDate = prevEnd;
          break;
        }
        case 'allTime':
          this.startDate = null;
          this.endDate = today;
      }
    },

    exportDonationList() {
      if (this.reportLoading) return;
      this.generating = true;
      this.reportLoading = true;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'report-generated-donation_report' });

      const params = {
        startDate: this.startDate,
        endDate: this.endDate
      };

      this.reportStore.exportDonationList(params)
        .then((res) => {
          // Get the created record and poll the report until complete
          const id = res.id;
          if (!id) {
            this.showPopup("Something went wrong!");
            this.reset();
          }
          return this.reportStore.pollReport(id)
            .then((record) => {
              // Report has been created, download the file

              this.reportStore.downloadCSVReport(record);
              this.showPopup("Finished!");
              this.reset();
            });
        }).catch((e) => {
          console.warn('error generating donation list', e);
          this.showPopup(e?.message || "Something went wrong generating the report!");
          this.reset();
      });
    },

    reset() {
      this.generating = false;
      this.reportLoading = false;
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    }
  }
};
</script>

<template>
  <div>
    <div v-if="generating">
      <div class="flex flex-col w-full items-center p-8 mt-100">
        <loading-spinner size="small" />
        <div class="mt-10">
          Your report is generating and will download shortly ...
        </div>
        <div>Larger reports can take longer to generate. You can view their status in History any time.</div>
      </div>
    </div>
    <div
      v-else
      class="report-generator-container"
    >
      <div
        v-if="title || description"
        class="header"
      >
        <div class="headline-3-regular">
          {{ title }}
        </div>
        <div class="body-2-light">
          {{ description }}
        </div>
      </div>

      <div class="content-container">
        <div class="quick-select">
          <div class="quick-select-title flex flex-row gap-2">
            <h3>Select Date Range</h3>
            <span class="body-2-light">(Leave blank to export all historical gifts.)</span>
          </div>
          <div class="quick-select-buttons">
            <button
              type="button"
              class="quick-select-btn"
              @click="setDateRange('lastWeek')"
            >
              Last Week
            </button>
            <button
              type="button"
              class="quick-select-btn"
              @click="setDateRange('lastMonth')"
            >
              Last Month
            </button>
            <button
              type="button"
              class="quick-select-btn"
              @click="setDateRange('lastYear')"
            >
              Year to Date
            </button>
            <button
              type="button"
              class="quick-select-btn"
              @click="setDateRange('currentFiscal')"
            >
              Current Fiscal Year
            </button>
            <button
              type="button"
              class="quick-select-btn"
              @click="setDateRange('previousFiscal')"
            >
              Previous Fiscal Year
            </button>
            <button
              type="button"
              class="quick-select-btn"
              @click="setDateRange('allTime')"
            >
              All Time
            </button>
          </div>
        </div>
        <form
          class="form"
          @submit.prevent="exportDonationList"
        >
          <div class="form-group-container">
            <div class="form-group">
              <label
                for="startDate"
                class="form-label"
              >Start Date</label>
              <vue-date-picker
                id="start_date"
                ref="start-datepicker"
                v-model="startDate"
                :format="displayDate"
                :enable-time-picker="false"
                placeholder="Select"
                prevent-min-max-navigation
                auto-apply
                teleport
                class="max-w-[300px]"
              />
            </div>
            <div class="form-group">
              <label
                for="endDate"
                class="form-label"
              >
                End Date
              </label>
              <vue-date-picker
                id="end_date"
                ref="end-datepicker"
                v-model="endDate"
                :format="displayDate"
                :enable-time-picker="false"
                placeholder="Select"
                prevent-min-max-navigation
                auto-apply
                teleport
                class="max-w-[300px]"
              />
            </div>
          </div>
          <button
            type="submit"
            class="btn btn-filled mt-4"
            :disabled="generating"
          >
            <icon
              icon="cloud-arrow-down"
              color="white"
              size="24px"
            />
            <span>Download</span>
          </button>
        </form>
      </div>
    </div>
    <popup
      ref="popup"
      :message="popupMessage"
      :duration="3000"
    />
  </div>
</template>


<style lang="scss" scoped>

.report-generator-container {
  @apply w-full flex flex-col h-full max-h-full;

  @apply p-4;

  .content-container {
    @apply flex flex-col w-full gap-4;
    @apply border p-3 rounded-md bg-gray-50;
  }
}

.header {
  @apply pb-4 border-b-2 mb-4 border-gray-200;
}

.form-group-container {
  @apply flex flex-row w-full mt-4 gap-4;

  .form-group {

    .form-label {
      @apply text-sm font-medium text-gray-700;
    }
  }
}


.quick-select-title {
  @apply text-sm font-medium text-gray-700 mb-4;
}

.quick-select-buttons {
  @apply flex flex-wrap gap-2;
}

.quick-select-btn {
  @apply px-2 py-1 text-sm font-bold text-gray-700 bg-gray-100 hover:bg-default_secondary hover:text-white border border-gray-300 rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
}

.spinner {
  @apply w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin;
}
</style>
